import { z } from "zod";

// Static envs (managed in `.github/frontend-envs.json` `envs.<env>.frontend`)
// plus the feature-env shape `ft-<numeric-ticket>` that the FeatureEnvStack
// writes at deploy time. Pattern matches the validation in
// `.github/workflows/feature-env-lifecycle.yml` (`^ft-[0-9]+$`); examples:
// `ft-12358`. Consumers that only care about `local` continue to work
// (feature-envs are not local).
const StaticDeployEnvSchema = z.enum([
  "local",
  "dev",
  "stg",
  "partner",
  "prod",
]);
// Brand the feature-env regex so `z.infer<typeof DeployEnvSchema>` stays a
// discriminating union instead of collapsing to plain `string`. Downstream
// callers can still do `deployEnv === "local"` (literal narrows out the
// branded branch); a value like `ft-12358` is typed as
// `string & z.BRAND<"FeatureEnvName">` so it cannot be confused with an
// arbitrary string literal at compile time.
const FeatureEnvNameSchema = z
  .string()
  .regex(/^ft-[0-9]+$/)
  .brand<"FeatureEnvName">();

const DeployEnvSchema = z.union([StaticDeployEnvSchema, FeatureEnvNameSchema]);

export type DeployEnv = z.infer<typeof DeployEnvSchema>;

export const RuntimeConfigSchema = z
  .object({
    VITE_DEPLOY_ENV: DeployEnvSchema,
    VITE_GRAPHQL_URL: z.string().url(),
    // The frontend's own origin (e.g. "https://www.stg.meistersystems.de"),
    // mirroring `VITE_WIZARD_ORIGIN` in apps/frontend-wizard.
    // `window.location.origin` isn't a usable substitute on native
    // Capacitor builds, where `window.location.hostname` is always
    // "localhost". Optional: the infra deploy stage that writes web's
    // `/config.json` doesn't produce this field yet, so CallAssistant.tsx
    // falls back to `window.location` when it's absent.
    VITE_APP_ORIGIN: z.string().url().optional(),
    VITE_KEYCLOAK_CLIENT: z.string().min(1),
    VITE_THREED_PLANNER_URL: z.string().url(),
    VITE_ICAL_GENERATOR_ENDPOINT: z.string().url(),
    VITE_EMBEDDABLE_WIZARD_URL: z.string().url(),
    // Public client-exposed by design; protected by GCP HTTP referrer
    // restriction on the API key. See MEIS-11514 for the operational
    // tracking ticket of an edge case (PDF generator has no Referer).
    VITE_GOOGLE_MAPS_API_KEY: z.string().min(1),
    // Forthcoming sibling: the iOS Capacitor build needs a separate Google
    // Maps API key because GCP's iOS-bundle-ID application restriction is
    // mutually exclusive with the HTTP-referrer restriction used for the
    // web key above. Declared `optional()` ahead of the rollout so the
    // schema accepts payloads with or without it the moment any env's
    // deploy stage starts writing the key — same coordination-avoiding
    // pattern as the deprecated transitional block further down. No
    // consumer reads this field yet. `.min(1)` matches the sibling
    // `VITE_GOOGLE_MAPS_API_KEY` so a mis-provisioned env writing an
    // empty string still fails validation.
    //
    // TODO(MEIS-12455/google-maps-keys): tighten to required, wire the
    // iOS Capacitor consumer, and add the key to
    // `.github/frontend-envs.json`.
    VITE_GOOGLE_MAPS_API_KEY_IOS: z.string().min(1).optional(),
    // Per-build identity, written by the deploy stage of the CodePipeline
    // (or by `write-mobile-config.ts` for Capacitor mobile builds) to the
    // commit SHA of the deployed Frontend image. Optional during the
    // MEIS-12358 Phase 8 rollout — old infra deploy stages don't yet
    // write the field; callers read it through `getVersion()` in
    // `environment.ts`, which returns `undefined` when absent and lets
    // UI surfaces fall back to "unknown" (the legacy build-time
    // `VERSION` const was removed in Phase 8). Once every env's deploy
    // stage writes `version`, this can be tightened to required.
    version: z.string().min(1).optional(),

    // --- Deprecated transitional keys (MEIS-12455) ---
    // The two keys below were removed from the schema in MEIS-12455, but
    // already-deployed `/config.json` payloads in S3 still contain them.
    // The schema uses `.strict()`, so unknown keys raise an
    // `unrecognized_keys` Zod error and the bootstrap renders "The
    // application configuration could not be loaded". Re-declaring them as
    // optional makes the schema accept both shapes (with and without the
    // legacy keys) for a transition window, without reintroducing any
    // reader of the values. No application code SHALL read these fields.
    //
    // TODO(MEIS-12455, remove after 2026-09-08): once every env's deploy
    // stage has re-written `/config.json` without these keys (the
    // source-of-truth `.github/frontend-envs.json` no longer lists them),
    // delete both fields from this schema and from `RuntimeConfig`.
    //
    // TODO(MEIS-12455, remove after 2026-09-08): legacy lambda-backend
    // toggle dropped in MEIS-12455.
    VITE_GRAPHQL_LAMBDA_URL: z.string().optional(),
    // TODO(MEIS-12455, remove after 2026-09-08): legacy Capacitor backend
    // override dropped in MEIS-12455.
    VITE_CAPACITOR_GRAPHQL_URL: z.string().optional(),
  })
  .strict();

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

let runtimeConfig: RuntimeConfig | undefined;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // Use default mode + credentials (same-origin, credentials=same-origin)
  // so the browser sends auth cookies the way it does for index.html
  // itself — auth-protected deployments would otherwise 401 on this
  // request. Must match the `<link rel="preload" href="/config.json"
  // as="fetch">` in the entrypoint HTML (no `crossorigin` attribute,
  // for the same reason) so the preloaded response is reused.
  const response = await fetch("/config.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch /config.json: ${response.status} ${response.statusText}`
    );
  }
  const payload: unknown = await response.json();
  runtimeConfig = RuntimeConfigSchema.parse(payload);
  return runtimeConfig;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!runtimeConfig) {
    throw new Error(
      "Runtime config not loaded. loadRuntimeConfig() must complete before getRuntimeConfig() is called. " +
        "If this fires inside a test, call setRuntimeConfigForTesting() in setup."
    );
  }
  return runtimeConfig;
}

export function setRuntimeConfigForTesting(config: RuntimeConfig): void {
  runtimeConfig = config;
}

export function clearRuntimeConfigForTesting(): void {
  runtimeConfig = undefined;
}
